import React, { useState } from 'react';
import { Menu, X } from 'lucide-react';
import { NavLink, Link } from 'react-router-dom';
import { LOGO_URL } from '../../data/siteData';

const navLinks = [
  { to: '/', label: 'Accueil', end: true },
  { to: '/services', label: 'Services' },
  { to: '/galerie', label: 'Galerie' },
  { to: '/etablissements', label: 'Nos établissements' },
  { to: '/recrutement', label: 'Recrutement' },
  { to: '/contact', label: 'Contact' }
];

const linkBaseClass = 'transition-colors font-medium text-sm px-1 py-1';
const linkClass = ({ isActive }) =>
  `${linkBaseClass} ${isActive ? 'text-[#0205B3] border-b-2 border-[#0205B3]' : 'text-gray-700 hover:text-[#0205B3]'}`;

export const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeMobile = () => setMobileOpen(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-blue-100" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* TOP ROW: logo + name + (desktop nav + contact button) + (mobile hamburger) */}
        <div className="flex justify-between items-center h-24 sm:h-32">
          <Link to="/" className="flex items-center space-x-3 sm:space-x-4" onClick={closeMobile} data-testid="header-home-link">
            <img
              src={LOGO_URL}
              alt="Logo AQUABLUENET Pressing Longpont-sur-Orge & Mennecy"
              className="h-20 w-20 sm:h-28 sm:w-28 lg:h-32 lg:w-32 object-contain drop-shadow-lg sm:-my-2"
              data-testid="header-logo"
            />
            <div>
              <h1 className="text-xl sm:text-3xl lg:text-4xl font-black text-gray-900 tracking-tight leading-none">AQUABLUENET</h1>
              <p className="text-xs sm:text-base text-[#0205B3] font-bold mt-0.5 sm:mt-1">Pressing & Soin Textile</p>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden xl:flex items-center space-x-8" data-testid="main-nav">
            {navLinks.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} className={linkClass}>
                {l.label}
              </NavLink>
            ))}
          </nav>

          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={mobileOpen}
            aria-controls="mobile-menu"
            className="xl:hidden inline-flex items-center justify-center p-2 rounded-lg text-[#0205B3] bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-[#0205B3] transition-colors"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
          </button>
        </div>

        {/* CleanWay Badge centered under nav - desktop only */}
        <div className="hidden xl:flex justify-center pb-3 -mt-1">
          <div className="inline-flex items-center bg-green-600 text-white px-4 py-1.5 rounded-full shadow-md animate-pulse" data-testid="cleanway-badge">
            <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="font-bold text-xs tracking-wide">Carte <strong>CleanWay</strong> 5àSec business acceptée dans nos 2 pressings</span>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        {mobileOpen && (
          <div
            id="mobile-menu"
            className="xl:hidden border-t border-blue-100 py-4"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-center mb-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow animate-pulse">
              <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="font-bold text-sm tracking-wide"><strong>CleanWay</strong> 5àSec business acceptée</span>
            </div>

            <nav className="flex flex-col space-y-1" data-testid="mobile-nav">
              {navLinks.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  onClick={closeMobile}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-lg font-medium transition-colors text-base ${isActive ? 'bg-blue-50 text-[#0205B3]' : 'text-gray-800 hover:bg-blue-50 hover:text-[#0205B3]'}`
                  }
                  data-testid={`mobile-nav-${l.to.replace('/', '') || 'home'}`}
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
